import { useEffect, useState } from 'react';

import type { TMyAssignmentTracksItem } from '@crehana/compass.dashlets/my-assignment-tracks-widget';
import { toast } from '@crehana/compass.ui/toaster';
import { useTranslation } from '@crehana/i18n';

import { useGetMyLearningPathsQueryQuery } from './graphql/get-my-learning-paths-query.v5.generated';
import { getLearningPathsMap, getNodeData } from './utils';

type TPageInfo = {
  endCursor: string;
  hasNextPage: boolean;
};

type TUseMyLearningPathsQueryProps = {
  pageSize?: number;
  search?: string;
};

const DEFAULT_PAGE_SIZE = 5;
const ERROR_TYPES = [
  'FoundationHomeUserIntegrityError',
  'FoundationHomeUserInternalError',
];

export const useMyLearningPathsQuery = (
  props: TUseMyLearningPathsQueryProps,
) => {
  const { t } = useTranslation();
  const [loadingMore, setLoadingMore] = useState(false);
  const [learningPaths, setLearningPaths] = useState<TMyAssignmentTracksItem[]>(
    [],
  );
  const [pageInfo, setPageInfo] = useState<TPageInfo | undefined>(undefined);

  const {
    data,
    loading,
    error: errorQuery,
    refetch,
    fetchMore,
  } = useGetMyLearningPathsQueryQuery({
    variables: {
      input: {
        pagination: { first: props.pageSize || DEFAULT_PAGE_SIZE },
        search: { query: props.search },
      },
    },
    notifyOnNetworkStatusChange: true,
    context: { clientName: 'v5' },
    onError: () => toast.error(t('ERROR_FETCHING_LEARNING_PATHS')),
  });

  const error =
    ERROR_TYPES.includes(
      data?.foundation_home_user?.get_my_learning_paths?.__typename ?? '',
    ) || !!errorQuery;

  const fetchMoreLearningPaths = async () => {
    if (!pageInfo?.hasNextPage) {
      return;
    }

    try {
      setLoadingMore(true);
      const { data: dataMore } = await fetchMore({
        variables: {
          input: {
            pagination: {
              first: props.pageSize || DEFAULT_PAGE_SIZE,
              after: pageInfo?.endCursor,
            },
            search: { query: props.search },
          },
        },
      });

      const nodeData = getNodeData(dataMore);
      const learningPathsMap = getLearningPathsMap(dataMore);

      setLearningPaths(prev => [...prev, ...learningPathsMap]);
      setPageInfo({
        endCursor: nodeData?.page_info?.end_cursor || '',
        hasNextPage: nodeData?.page_info?.has_next_page || false,
      });
    } catch (error) {
      toast.error(t('ERROR_FETCHING_LEARNING_PATHS'));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!data) {
      return;
    }

    const nodeData = getNodeData(data);
    const learningPathsMap = getLearningPathsMap(data);

    setLearningPaths(learningPathsMap);
    setPageInfo({
      endCursor: nodeData?.page_info?.end_cursor || '',
      hasNextPage: nodeData?.page_info?.has_next_page || false,
    });
  }, [data]);

  return {
    loading,
    loadingMore,
    learningPaths,
    tracksStarted: getNodeData(data)?.path_started || undefined,
    error,
    refetch,
    fetchMoreLearningPaths,
  };
};
